import React, { useEffect, useState } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import { getAdminPayouts, updatePayout } from '../../api/client';
import { useToast } from '../../contexts/ToastContext';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import { DollarSign, Loader2, CheckCircle, XCircle, Clock } from 'lucide-react';

export default function AdminPayouts(): React.JSX.Element {
  const toast = useToast();
  const [payouts, setPayouts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('pending');

  useEffect(() => {
    loadPayouts();
  }, [statusFilter]);

  const loadPayouts = async () => {
    setLoading(true);
    try {
      const res = await getAdminPayouts(statusFilter);
      setPayouts(res.payouts || []);
    } catch (err: any) {
      toast.show(err.message || 'Failed to load payouts', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async (id: number, status: 'processing' | 'paid' | 'rejected') => {
    try {
      await updatePayout(id, { status });
      toast.show(`Payout marked as ${status}`, 'success');
      loadPayouts();
    } catch (err: any) {
      toast.show(err.message || 'Failed to update', 'error');
    }
  };

  const statusIcon = (status: string) => {
    switch (status) {
      case 'paid': return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'rejected': return <XCircle className="w-4 h-4 text-red-500" />;
      case 'processing': return <Clock className="w-4 h-4 text-amber-500" />;
      default: return <Clock className="w-4 h-4 text-yellow-500" />;
    }
  };

  const totalPending = payouts
    .filter((p) => p.status === 'pending')
    .reduce((sum, p) => sum + parseFloat(p.amount), 0);

  return (
    <AdminLayout>
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <DollarSign className="w-7 h-7 text-green-600" />
          <h1 className="text-2xl font-bold text-slate-900">Payout Management</h1>
        </div>

        <Card className="mb-6 flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-yellow-100 flex items-center justify-center">
            <DollarSign className="w-6 h-6 text-yellow-600" />
          </div>
          <div>
            <p className="text-sm text-slate-500">Total Pending</p>
            <p className="text-2xl font-bold text-slate-900">₹{totalPending.toLocaleString()}</p>
          </div>
        </Card>

        <div className="flex gap-2 mb-4">
          {['pending', 'processing', 'paid', 'rejected'].map((s) => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                statusFilter === s ? 'bg-amber-500 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              {s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
          </div>
        ) : payouts.length === 0 ? (
          <Card className="text-center py-12 text-slate-500">No payouts found.</Card>
        ) : (
          <div className="space-y-3">
            {payouts.map((payout) => (
              <Card key={payout.id} className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  {statusIcon(payout.status)}
                  <div>
                    <p className="font-semibold text-slate-900">{payout.trainer_name}</p>
                    <p className="text-xs text-slate-500">{payout.trainer_email}</p>
                    <p className="text-xs text-slate-400">{new Date(payout.created_at).toLocaleDateString()}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-lg font-bold text-slate-900">₹{parseFloat(payout.amount).toLocaleString()}</p>
                  <p className="text-xs text-slate-500 capitalize">{payout.payment_method}</p>
                </div>
                {payout.status === 'pending' && (
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => handleUpdate(payout.id, 'processing')}>
                      Process
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => handleUpdate(payout.id, 'rejected')}>
                      Reject
                    </Button>
                  </div>
                )}
                {payout.status === 'processing' && (
                  <Button size="sm" onClick={() => handleUpdate(payout.id, 'paid')}>
                    Mark Paid
                  </Button>
                )}
              </Card>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
